import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

/**
 * 📦 PROFILE MODULE CONFIGURATION
 * 
 * This file defines how the Profile module integrates into the application.
 * Each module has its own moduleConfig.ts file that tells the system:
 * - What navigation items to show
 * - What routes/pages are available  
 * - Who has permission to access what
 * - Module metadata (name, icon, version, etc.)
 */

// Import the profile components (using React.lazy for code splitting)
// These components will be loaded only when needed, improving performance
const ViewProfile = React.lazy(() => import("./ViewProfile"));
const EditProfile = React.lazy(() => import("./EditProfile"));
const Settings = React.lazy(() => import("./Settings"));
const ShippingAddresses = React.lazy(() => import("./ShippingAddresses"));
const PaymentMethods = React.lazy(() => import("./PaymentMethods"));
const SubscriptionOffers = React.lazy(() => import("./SubscriptionOffers"));
const CurrentSubscriptions = React.lazy(() => import("./CurrentSubscriptions"));

const profileModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY - Basic information about this module
    id: "profile",                           // Unique identifier - must match backend module registry
    name: "Profile Management",              // Human-readable name shown in UI
    description: "Manage your personal and business information", // Description for admin panels
    version: "1.0.0",                       // Version for deployment tracking
    enabled: true,                          // Whether this module is active (can be overridden by tenant config)
    category: "account",                    // Groups modules together (account, billing, inventory, etc.)
    order: 1,                              // Display order in menus (lower = appears first)
    icon: "👤",                            // Emoji icon for this module
    color: "#805AD5",                      // Brand color for this module's UI elements

    // ROUTES
    routes: [
        {
            path: "/profile",
            component: ViewProfile,
            permissionLevel: "view"
        },
        {
            path: "/profile/edit",
            component: EditProfile,
            permissionLevel: "edit"
        },
        {
            path: "/profile/settings",
            component: Settings,
            permissionLevel: "view"
        },
        {
            path: "/profile/shipping",
            component: ShippingAddresses,
            permissionLevel: "edit"
        },
        {
            path: "/profile/payment-methods",
            component: PaymentMethods,
            permissionLevel: "view"
        },
        {
            path: "/profile/subscription-offers",
            component: SubscriptionOffers,
            permissionLevel: "view"
        },
        {
            path: "/profile/subscriptions",
            component: CurrentSubscriptions,
            permissionLevel: "view"
        }
    ],

    // NAVIGATION
    navigation: [
        {
            label: "My Profile",
            path: "/profile",
            icon: "👤",
            permissionLevel: "view"
        },
        {
            label: "Shipping Addresses",
            path: "/profile/shipping",
            icon: "📦",
            permissionLevel: "edit"
        },
        {
            label: "Payment Methods",
            path: "/profile/payment-methods",
            icon: "💰",
            permissionLevel: "view"
        },
        {
            label: "Subscription Plans",
            path: "/profile/subscription-offers",
            icon: "🎯",
            permissionLevel: "view",
            badge: { text: "$1/day", color: "green" }
        },
        {
            label: "My Subscriptions",
            path: "/profile/subscriptions",
            icon: "📊",
            permissionLevel: "view"
        }
    ],

    // PERMISSIONS - Profile is accessible to all authenticated users
    permissions: {
        view: ['USER'],
        create: ['USER'],
        edit: ['USER'],
        delete: []
    },

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready profile management system",
                "✅ Complete user profile and settings management",
                "✅ Subscription and payment integration",
                "✅ Full TypeScript type safety",
                "✅ Responsive design across all devices"
            ],
            notes: "Official stable release with complete profile management features."
        },
        {
            version: "0.9.0",
            releaseDate: "2025-10-27",
            title: "Payment System Refactoring",
            features: [
                "🔨 Refactored payment receiving details to tenant model",
                "🔨 Streamlined payment configuration",
                "✨ Improved data structure for multi-tenant payments",
                "✨ Enhanced payment method management"
            ],
            notes: "Major refactoring to support tenant-specific payment configurations."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-10-07",
            title: "Code Organization & Custom Hooks",
            features: [
                "🔨 Created custom hooks for profile data management",
                "✨ Added useProfileData and useProfileUpdate hooks",
                "✨ Centralized GraphQL queries in /queries directory",
                "✨ Added TypeScript type definitions in /types directory",
                "✨ Created comprehensive refactoring documentation"
            ],
            notes: "Enhanced code organization with custom hooks and better type safety."
        },
        {
            version: "0.7.0",
            releaseDate: "2024-08-30",
            title: "EditProfile Component Optimization",
            features: [
                "🔨 Major refactoring of EditProfile component",
                "✨ Reduced component complexity (526 lines removed)",
                "✨ Improved form handling and validation",
                "✨ Enhanced user experience with better feedback"
            ],
            notes: "Significant code optimization improving maintainability and performance."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-08-29",
            title: "Module Streamlining",
            features: [
                "🔨 Removed BillingDetails component (moved to dedicated module)",
                "🔨 Removed EditWebsiteDetails component (deprecated)",
                "✨ Simplified ViewProfile component",
                "✨ Cleaner module structure and navigation"
            ],
            notes: "Streamlined profile module by moving specialized features to dedicated modules."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-08-23",
            title: "Account Security Features",
            features: [
                "✨ Added email change modal with verification",
                "✨ Added phone change modal with SMS verification",
                "🔒 Enhanced account security workflows",
                "✨ Improved user contact information management"
            ],
            notes: "Added secure email and phone number change capabilities."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-07-14",
            title: "Payment & Shipping Management",
            features: [
                "✨ Added BillingDetails component",
                "✨ Added PaymentMethods management",
                "✨ Added ShippingAddresses functionality",
                "✨ Enhanced moduleConfig with comprehensive routing",
                "✨ Integrated Stripe payment processing"
            ],
            notes: "Major feature expansion adding payment and shipping management."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-06-21",
            title: "Profile Photo & Website Settings",
            features: [
                "✨ Added profile photo upload to IPFS",
                "✨ Created EditWebsiteDetails component",
                "✨ Added Settings component for user preferences",
                "✨ Enhanced email and phone verification",
                "✨ Improved profile viewing experience"
            ],
            notes: "Added visual profile customization and website management features."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-06-21",
            title: "Initial Profile Management Features",
            features: [
                "✅ Created ViewProfile component",
                "✅ Created EditProfile component",
                "✅ Implemented basic moduleConfig structure",
                "✅ Added profile routing to application",
                "✅ Integrated with subscription management"
            ],
            notes: "Initial implementation of core profile management functionality."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default profileModuleConfig; 